<template>
  <div v-if="collectible && token">
    <v-container>
      <v-row justify="center" align="center">
        <v-col cols="5" align="right">
          <div class="collectibleArtist">
            {{ collectible.artist }}
          </div>
          <div class="collectibleName my-2">
            {{ collectible.name }}
          </div>
          <div class="momentName">
            {{ moment.name }}
          </div>
          <div class="mt-6">
            <v-card-actions class="ma-0 pa-0">
              <v-spacer />
              <div v-if="token.isPremium" class="premium mr-2">
                Premium
              </div>
              <div class="rarityText" :class="momentRarityClass">
                <v-icon class="mr-2">mdi-heart</v-icon>{{ momentRarityText }}
              </div>
            </v-card-actions>
          </div>
          <div class="collectibleDescription">
            {{ collectible.description }}
          </div>

          <div v-if="listing" class="priceText mt-8">{{ price }} GM Points</div>
          <v-btn
            v-if="isUntradable"
            class="blackButton claimButton"
            depressed
            text
          >
            Untradable
          </v-btn>
          <v-btn
            v-else-if="!listing && isOwner"
            @click="showSellDialog = true"
            class="yellowButton claimButton"
            depressed
            text
          >
            List for sale
            <v-icon style="margin-left: 16px; width: 20px; height: 20px;">
              mdi-arrow-right
            </v-icon>
          </v-btn>
          <v-btn
            v-else-if="isSeller"
            @click="$emit('cancelListing')"
            class="blackButton cancelButton"
            depressed
            text
          >
            Cancel Listing
          </v-btn>
          <v-btn
            v-else-if="listing && user"
            @click="
              user.points < listing.price
                ? $router.push('/shop')
                : (showBuyDialog = true)
            "
            class="yellowButton claimButton"
            depressed
            text
          >
            {{ user.points >= listing.price ? 'Buy Now' : 'Get More Points' }}
            <v-icon style="margin-left: 16px; width: 20px; height: 20px;">
              mdi-arrow-right
            </v-icon>
          </v-btn>
          <v-btn
            v-else-if="!user && listing"
            class="yellowButton claimButton"
            depressed
            text
            @click="$router.push(`/auth?source=${$route.path}`)"
          >
            Sign in to buy
            <v-icon style="margin-left: 16px; width: 20px; height: 20px;">
              mdi-arrow-right
            </v-icon>
          </v-btn>
        </v-col>
        <v-col cols="7">
          <video
            :poster="token.isPremium ? moment.foilImageUrl : moment.imageUrl"
            class="videoFrame"
            muted
            autoplay
            loop
          >
            <source :src="moment.videoUrl" type="video/webm" />
            Your browser does not support the video tag.
          </video>
        </v-col>
      </v-row>
      <CreateListingDialog
        v-model="showSellDialog"
        :collectible="collectible"
        :token="token"
        :isMobile="false"
        @listingComplete="$emit('listingComplete')"
      />
      <CreateOrderDialog
        v-if="listing"
        v-model="showBuyDialog"
        :collectible="collectible"
        :token="token"
        :listing="listing"
        :isMobile="false"
        @orderComplete="$emit('orderComplete')"
      />
    </v-container>
  </div>
  <v-container v-else class="fillHeight" />
</template>

<script>
  import { mapState } from 'vuex';

  export default {
    components: {
      CreateListingDialog: () =>
        import('../Marketplace/CreateListingDialog.vue'),
      CreateOrderDialog: () => import('../Marketplace/CreateOrderDialog.vue'),
    },

    props: ['collectible', 'token', 'listing'],

    data: () => {
      return {
        showSellDialog: false,
        showBuyDialog: false,
      };
    },

    computed: {
      ...mapState({
        firebaseUser: (state) => state.user,
      }),
      user() {
        if (this.firebaseUser) {
          return this.firebaseUser.user;
        }
        return null;
      },
      moment() {
        return this.token.moment;
      },
      momentRarityText() {
        if (
          this.token.moment.rarity === 'gmp' ||
          this.token.moment.rarity === 'special' ||
          this.token.moment.rarity === 'instant'
        ) {
          return 'Golden';
        }
        return this.token.moment.rarity;
      },
      momentRarityClass() {
        if (
          this.token.moment.rarity === 'gmp' ||
          this.token.moment.rarity === 'special' ||
          this.token.moment.rarity === 'instant'
        ) {
          return 'instantBubble';
        }
        if (this.token.moment.rarity !== 'common') {
          return 'rareBubble';
        }
        return 'commonBubble';
      },
      isSeller() {
        return (
          this.user &&
          this.listing &&
          this.listing.seller.toString() === this.user._id.toString()
        );
      },
      isOwner() {
        return (
          this.user &&
          this.token &&
          this.token.owner &&
          this.token.owner.toString() === this.user._id.toString()
        );
      },
      collectibleId() {
        return this.$route.params.collectibleId;
      },
      tokenId() {
        return this.$route.params.tokenId;
      },
      price() {
        if (!this.listing) {
          return null;
        }
        return `${this.listing.price}`;
      },
      isUntradable() {
        if (this.token.isClaimed) {
          return true;
        }
        if (
          this.moment.rarity === 'instant' ||
          this.moment.rarity === 'special' ||
          this.moment.rarity === 'gmp'
        ) {
          return (
            !this.collectible.claimExpiryDate ||
            this.$moment(this.collectible.claimExpiryDate) <= Date.now()
          );
        }
        return false;
      },
    },
  };
</script>

<style scoped>
  .premium {
    background: #9b59b6;
    font-family: 'Work Sans' !important;
    font-style: bold !important;
    font-weight: 700 !important;
    font-size: 14px !important;
    text-transform: uppercase;
    text-align: center;
    color: #fff;

    width: 123px;
    height: 36px;
    border-radius: 18px;
    justify-content: center;
    align-items: center;
    display: flex;
  }
  .collectibleArtist {
    font-family: 'Work Sans' !important;
    font-style: normal !important;
    font-weight: 600 !important;
    font-size: 24px !important;
    line-height: 24px !important;
    color: #151515 !important;
  }
  .collectibleName {
    font-family: 'Work Sans' !important;
    font-style: bold !important;
    font-weight: 500 !important;
    font-size: 24px !important;
    line-height: 24px !important;

    color: #151515;
  }
  .momentName {
    font-family: 'Work Sans' !important;
    font-style: bold !important;
    font-weight: 700 !important;
    font-size: 42px !important;
    line-height: 42px !important;

    color: #151515;
  }

  .rarityText {
    font-family: 'Work Sans' !important;
    font-style: bold !important;
    font-weight: 700 !important;
    font-size: 14px !important;
    text-transform: uppercase;
    color: #151515;
  }

  .commonBubble {
    width: 123px;
    height: 36px;
    border-radius: 18px;
    background: #c0c0c0;
    justify-content: center;
    align-items: center;
    display: flex;
  }

  .rareBubble {
    width: 123px;
    height: 36px;
    border-radius: 18px;
    background: linear-gradient(94deg, #ffb8e7 3.41%, #baeeff 84.75%);
    justify-content: center;
    align-items: center;
    display: flex;
  }

  .instantBubble {
    width: 123px;
    height: 36px;
    border-radius: 18px;
    background: #fed85d;
    justify-content: center;
    align-items: center;
    display: flex;
  }
  .collectibleDescription {
    margin-left: 100px;
    margin-top: 24px;
    font-family: 'Work Sans' !important;
    font-style: normal !important;
    font-weight: 400 !important;
    font-size: 16px !important;
    line-height: 24px !important;
    letter-spacing: 0.003em !important;

    color: #151515;
  }

  .freeText {
    margin-top: 32px;
    font-family: 'Work Sans' !important;
    font-style: normal !important;
    font-weight: 700 !important;
    font-size: 24px !important;
    line-height: 32px !important;

    color: #151515;
  }
  .priceText {
    font-family: 'Work Sans' !important;
    font-style: bold !important;
    font-weight: 700 !important;
    font-size: 24px !important;
    line-height: 24px !important;

    color: #151515;
  }

  .claimButton {
    margin-top: 32px !important;
    margin-right: 0px !important;
    width: 294px !important;
    height: 64px !important;
  }
  .cancelButton {
    margin-top: 32px !important;
    margin-right: 0px !important;
    width: 294px !important;
    height: 64px !important;
  }

  .quantityText {
    margin-top: 32px;
    box-sizing: border-box;

    /* Auto layout */

    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 12px 24px;
    gap: 24px;

    width: 167px;
    height: 56px;

    background: rgba(235, 235, 235, 0.2);
    /* Colour System 2/Shade/shade-100 */

    border: 1px solid #c0c0c0;
    border-radius: 40px;

    font-family: 'Work Sans' !important;
    font-style: normal !important;
    font-weight: 500 !important;
    font-size: 16px !important;
    line-height: 32px !important;

    color: #151515;
  }

  .sectionTitle {
    margin-bottom: 36px;
    font-family: 'Work Sans' !important;
    font-style: normal !important;
    font-weight: 700 !important;
    font-size: 45px !important;
    line-height: 52px !important;
    color: #000000;
  }
</style>
